import { StripeAccountStatus } from "@/modules/payment/stripeTypes"
import { type UserProfileApiGateway } from "@/modules/users/gateway/usersApiGateway"
import { type UserProfileDraftDto, type UserProfileDto } from "@/modules/users/gateway/usersDto"
import { GenderCategory } from "@modules/profile/publicProfile/gateway/publicProfileDto"
import { USER_TEST_DATA } from "@modules/profile/publicProfile/test/publicProfileApi.fake"

const _ATHLETE_FAKE_DATA: UserProfileDto = {
  ...USER_TEST_DATA.ATHLETE,
  email: "athlete.default@email.com",
  locale: "en",
  inscriptionDate: "2024-01-01T00:00:00.000Z",
  notificationsEnabled: true,
  stripeAccountStatus: StripeAccountStatus.UNDEFINED,
  avatar: null,
  nationality: null,
  job: null,
  biography: null,
  birthdate: null,
  address: null,
  phoneNumber: null,
  weight: null,
  height: null,
  crossfitStartDate: null,
  genderCategory: null,
  emergencyInformation: null,
  badges: [],
  boxesStaffMember: [],
  stripeAccountId: null,
  stripeAccountCurrency: null,
  stripeMinimumAmount: null,
  stripeMaximumAmount: null,
}

const _HEAD_COACH_FAKE_DATA: UserProfileDto = {
  ...USER_TEST_DATA.HEAD_COACH,
  email: "headcoach.default@email.com",
  locale: "en",
  inscriptionDate: "2024-01-01T00:00:00.000Z",
  notificationsEnabled: true,
  stripeAccountStatus: StripeAccountStatus.OK,
  stripeAccountCurrency: "EUR",
  avatar: null,
  nationality: null,
  job: null,
  biography: null,
  birthdate: null,
  address: null,
  phoneNumber: null,
  weight: null,
  height: null,
  crossfitStartDate: null,
  genderCategory: null,
  emergencyInformation: null,
  badges: [],
  boxesStaffMember: [],
  stripeAccountId: null,
  stripeMinimumAmount: null,
  stripeMaximumAmount: null,
}

const _SHARED_COACH_FAKE_DATA: UserProfileDto = {
  ...USER_TEST_DATA.COACH,
  email: "Coach.default@email.com",
  locale: "en",
  inscriptionDate: "2024-01-01T00:00:00.000Z",
  notificationsEnabled: true,
  stripeAccountStatus: StripeAccountStatus.UNDEFINED,
  avatar: null,
  nationality: null,
  job: null,
  biography: null,
  birthdate: null,
  address: null,
  phoneNumber: null,
  weight: null,
  height: null,
  crossfitStartDate: null,
  genderCategory: null,
  emergencyInformation: null,
  badges: [],
  boxesStaffMember: [],
  stripeAccountId: null,
  stripeAccountCurrency: null,
  stripeMinimumAmount: null,
  stripeMaximumAmount: null,
}

const _ATHLETE_FILLED_PROFILE_FAKE_DATA: UserProfileDto = {
  ...USER_TEST_DATA.ATHLETE_COMPLETE_PROFILE,
  email: "athlete.rich_profile@email.com",
  locale: "en",
  inscriptionDate: "2024-01-01T00:00:00.000Z",
  notificationsEnabled: true,
  stripeAccountStatus: StripeAccountStatus.UNDEFINED,
  stripeAccountId: null,
  stripeAccountCurrency: null,
  stripeMinimumAmount: null,
  stripeMaximumAmount: null,
  username: "athlete_rich_profile",
  avatar: {
    key: "key",
    url: "path",
    hash: "hash",
  },
  nationality: {
    value: "French",
    visibility: { all: true },
  },
  job: {
    value: "Developer",
    visibility: { all: true },
  },
  biography: "I am a developer",
  birthdate: {
    value: "01/01/1990",
    visibility: { all: true },
  },
  address: {
    address: "1 rue de Rivoli",
    city: "Paris",
    country: "France",
    postalCode: "75001",
    additionalInformation: null,
  },
  phoneNumber: {
    countryCode: "FR",
    number: "0123456789",
  },
  weight: {
    value: 70,
    visibility: { all: true },
  },
  height: {
    value: 180,
    visibility: { all: true },
  },
  crossfitStartDate: {
    value: "01/01/2020",
    visibility: { all: true },
  },
  genderCategory: {
    value: GenderCategory.FEMALE,
    visibility: { all: true },
  },
  emergencyInformation: "I am allergic to peanuts",
  badges: [],
  boxesStaffMember: [],
}

const _ATHLETE_PARTIAL_PROFILE: UserProfileDto = {
  ..._ATHLETE_FILLED_PROFILE_FAKE_DATA,
  biography: null,
  phoneNumber: null,
}

const _ATHLETE_WITHOUT_AVATAR_PROFILE: UserProfileDto = {
  ..._ATHLETE_FILLED_PROFILE_FAKE_DATA,
  avatar: null,
}

export const USER_PROFILE_TEST_DATA = {
  DEFAULT: _ATHLETE_FAKE_DATA,
  ATHLETE: _ATHLETE_FAKE_DATA,
  ATHLETE_COMPLETE_PROFILE: _ATHLETE_FILLED_PROFILE_FAKE_DATA,
  ATHLETE_PARTIAL_PROFILE: _ATHLETE_PARTIAL_PROFILE,
  ATHLETE_WITHOUT_AVATAR_PROFILE: _ATHLETE_WITHOUT_AVATAR_PROFILE,
  HEAD_COACH: _HEAD_COACH_FAKE_DATA,
  SHARED_COACH: _SHARED_COACH_FAKE_DATA,
}

export class FakeUserProfileApi implements UserProfileApiGateway {
  user: UserProfileDto | null = USER_PROFILE_TEST_DATA.DEFAULT
  lastUserProfileCreated: Partial<UserProfileDraftDto> | null = null

  givenUser(user: UserProfileDto | null) {
    this.user = user
  }

  getUser(): Promise<UserProfileDto> {
    if (!this.user) {
      return Promise.reject(new Error("User not found"))
    }
    return Promise.resolve(this.user)
  }

  register(draft: Partial<UserProfileDraftDto>): Promise<UserProfileDto> {
    this.lastUserProfileCreated = draft
    return Promise.resolve(USER_PROFILE_TEST_DATA.DEFAULT)
  }

  update(_user: Partial<UserProfileDraftDto>): Promise<UserProfileDto> {
    return Promise.resolve(USER_PROFILE_TEST_DATA.DEFAULT)
  }

  checkUsername(_username: string) {
    return Promise.resolve(true)
  }
}
